import React, { useState, useEffect } from "react";

interface RecordType {
  id?: string;
  [key: string]: any;
}

interface EditModalProps {
  record: RecordType;
  columns: string[];
  collectionName: string;
  onSave: (updatedRecord: RecordType) => void;
  onClose: () => void;
  onDeleteField?: (field: string) => void;
  onDeleteRecord?: (id: string) => void; // dodaj callback do usuwania rekordu
}

// Funkcja pomocnicza do konwersji stopni na radiany
function degreesToRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

// Funkcja pomocnicza do konwersji radianów na stopnie
function radiansToDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

/**
 * Oblicza rozstaw osi roweru (wheelbase) na podstawie geometrii ramy
 * Zaimplementowano zaawansowany algorytm uwzględniający kąt główki ramy i geometrię widelca
 */
function calculateWheelbase(
  chainstayMm: number,
  bbDrop: number,
  stack: number,
  reach: number,
  headAngleDegrees: number,
  rake: number,
  headTubeLengthMm: number
): { wheelbase: number; error?: string } {
  try {
    // Konwersja kąta główki ramy na radiany
    const headAngleRad = degreesToRadians(90 - headAngleDegrees);

    // Sprawdzamy poprawność danych wejściowych
    if (headAngleRad <= 0 || headAngleRad >= Math.PI/2) {
      return {
        wheelbase: 0,
        error: `Error: Invalid head tube angle (${headAngleDegrees}°). Should be between 0° and 90°.`
      };
    }

    // Obliczenia poszczególnych składowych zgodnie z algorytmem

    // A: Pozioma odległość między supportem a osią tylnego koła (z tw. Pitagorasa)
    const underRoot = chainstayMm * chainstayMm - bbDrop * bbDrop;
    if (underRoot < 0) {
      return {
        wheelbase: 0,
        error: `Error: Invalid data - chainstay (${chainstayMm}mm) must be greater than bbDrop (${bbDrop}mm)`
      };
    }
    const A = Math.sqrt(underRoot);

    // B: Reach - już podany jako parametr
    const B = reach;

    // C: Oblicz z tangensa kąta główki ramy i odległości (STACK - bbDROP)
    const stackMinusBbDrop = stack - bbDrop;
    if (stackMinusBbDrop <= 0) {
      return {
        wheelbase: 0,
        error: `Error: Value (stack - bbDrop) should be positive. Stack: ${stack}mm, bbDrop: ${bbDrop}mm`
      };
    }
    const C = stackMinusBbDrop * Math.tan(headAngleRad);

    // D: Pozioma składowa rake, obliczona z cosinusa kąta główki
    const D = rake * Math.cos(headAngleRad);

    // H: Pionowa odległość pomiędzy punktem rake a linią wheelbase
    const H = rake * Math.sin(headAngleRad);

    // TTFW: Odległość od górnego punktu główki ramy do tymczasowej osi przedniego koła
    const TTFW = Math.sqrt(stackMinusBbDrop * stackMinusBbDrop + (B + C) * (B + C));

    // Sprawdzamy czy długość główki jest dostępna i prawidłowa
    if (!headTubeLengthMm || headTubeLengthMm <= 0 || headTubeLengthMm >= TTFW) {
      // Jeśli nie ma długości główki lub jest nieprawidłowa, używamy prostszej metody
      // E będzie zerowe w tym przypadku
      const wheelbase = A + B + C + D;
      console.log("Obliczanie wheelBase bez uwzględniania kąta widelca (brak lub nieprawidłowa długość główki ramy)");
      console.log(`A=${A.toFixed(2)}, B=${B.toFixed(2)}, C=${C.toFixed(2)}, D=${D.toFixed(2)}`);

      return {
        wheelbase: Math.round(wheelbase * 10) / 10  // Zaokrąglamy do 1 miejsca po przecinku
      };
    }

    // KW: Kąt nachylenia widelca
    const effectiveForkLength = TTFW - headTubeLengthMm;
    if (effectiveForkLength <= 0) {
      return {
        wheelbase: 0,
        error: `Error: Invalid head tube length (${headTubeLengthMm}mm)`
      };
    }

    const forkAngleRad = Math.atan(rake / effectiveForkLength);

    // E: Ostatni składnik wheelbase wynikający z kąta widelca
    const combinedAngleRad = headAngleRad + forkAngleRad;
    const E = H * Math.tan(combinedAngleRad);

    // Suma wszystkich składowych daje wheelbase
    const wheelbase = A + B + C + D + E;

    // Logowanie wyników pośrednich do celów diagnostycznych
    console.log("Składowe wheelBase:");
    console.log(`A (tylna pozioma odległość): ${A.toFixed(2)}mm`);
    console.log(`B (reach): ${B.toFixed(2)}mm`);
    console.log(`C (projekcja stack-bbDrop): ${C.toFixed(2)}mm`);
    console.log(`D (pozioma składowa rake): ${D.toFixed(2)}mm`);
    console.log(`H (pionowa składowa rake): ${H.toFixed(2)}mm`);
    console.log(`TTFW: ${TTFW.toFixed(2)}mm`);
    console.log(`Kąt widelca: ${radiansToDegrees(forkAngleRad).toFixed(2)}°`);
    console.log(`E (dodatkowa składowa): ${E.toFixed(2)}mm`);
    console.log(`Całkowity wheelBase: ${wheelbase.toFixed(2)}mm`);
    console.log(`stack - bbdrop: ${stackMinusBbDrop.toFixed(2)}mm`);
    console.log(`kąt główki: ${headAngleDegrees.toFixed(2)}mm`);
    console.log(`kąt główkiRAD: ${headAngleRad.toFixed(2)}mm`);

    // Zwróć wynik z zaokrągleniem do 1 miejsca po przecinku
    return { wheelbase: Math.round(wheelbase * 10) / 10 };
  } catch (error) {
    console.error("Błąd podczas obliczania wheelbase:", error);
    return {
      wheelbase: 0,
      error: `Calculation error: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

// Standardowa nazwa pola dla wheelBase (z dużą literą B)
const WHEELBASE_FIELD_NAME = "wheelBase";

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).replace(",", ".").match(/-?\d+(\.\d+)?/);
  if (!normalized) return null;
  const parsed = Number(normalized[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

const FrameSizePreview: React.FC<{ record: RecordType; width?: number; height?: number }> = ({
  record,
  width = 340,
  height = 150
}) => {
  const wheelSizeInches = toNumber(record.wheelSizeInches) ?? 28;
  const wheelRadiusMm = Math.max(200, (wheelSizeInches * 25.4) / 2);
  const chainstayMm = toNumber(record.chainstayMm) ?? 410;
  const bbDropMm = toNumber(record.bbDrop) ?? 70;
  const reachMm = toNumber(record.reach) ?? 380;
  const stackMm = toNumber(record.stack) ?? 560;
  const seatTubeLengthMm = toNumber(record.seatTubeLengthMm) ?? 500;
  const headTubeLengthMm = toNumber(record.headTubeLengthMm) ?? 140;
  const seatAngleDeg = toNumber(record.seatAngleDegrees) ?? 73;
  const headAngleDeg = toNumber(record.headAngleDegrees) ?? 73;
  const rakeMm = toNumber(record.rake) ?? 45;

  const seatAngleRad = (seatAngleDeg * Math.PI) / 180;
  const headAngleRad = (headAngleDeg * Math.PI) / 180;

  const rearAxle = { x: 0, y: 0 };
  const bbX = Math.sqrt(Math.max(0, chainstayMm * chainstayMm - bbDropMm * bbDropMm));
  const bb = { x: bbX, y: -bbDropMm };
  const seatTop = {
    x: bb.x - Math.cos(seatAngleRad) * seatTubeLengthMm,
    y: bb.y + Math.sin(seatAngleRad) * seatTubeLengthMm
  };
  const headTop = {
    x: bb.x + reachMm,
    y: bb.y + stackMm
  };
  const headBottom = {
    x: headTop.x + Math.cos(headAngleRad) * headTubeLengthMm,
    y: headTop.y - Math.sin(headAngleRad) * headTubeLengthMm
  };

  const storedWheelBase = toNumber(record.wheelBase);
  const computedWheelBase = bbX + reachMm + (stackMm - bbDropMm) * Math.tan((Math.PI / 2) - headAngleRad) + rakeMm * Math.cos((Math.PI / 2) - headAngleRad);
  const wheelBaseMm = Math.max(bbX + 250, storedWheelBase ?? computedWheelBase);
  const frontAxle = { x: wheelBaseMm, y: 0 };

  const points = [rearAxle, frontAxle, bb, seatTop, headTop, headBottom];
  const minX = Math.min(...points.map(p => p.x), rearAxle.x - wheelRadiusMm, frontAxle.x - wheelRadiusMm);
  const maxX = Math.max(...points.map(p => p.x), rearAxle.x + wheelRadiusMm, frontAxle.x + wheelRadiusMm);
  const minY = Math.min(...points.map(p => p.y), -wheelRadiusMm);
  const maxY = Math.max(...points.map(p => p.y), wheelRadiusMm);

  const svgWidth = width;
  const svgHeight = height;
  const margin = 6;
  const contentWidth = svgWidth - margin * 2;
  const contentHeight = svgHeight - margin * 2;
  const widthMm = Math.max(1, maxX - minX);
  const heightMm = Math.max(1, maxY - minY);
  const scale = Math.min(contentWidth / widthMm, contentHeight / heightMm);

  const px = (point: { x: number; y: number }) => ({
    x: margin + (point.x - minX) * scale,
    y: margin + (maxY - point.y) * scale
  });

  const rearAxlePx = px(rearAxle);
  const frontAxlePx = px(frontAxle);
  const bbPx = px(bb);
  const seatTopPx = px(seatTop);
  const headTopPx = px(headTop);
  const headBottomPx = px(headBottom);
  const wheelRadiusPx = Math.max(5, wheelRadiusMm * scale);

  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      aria-label={`Rama ${String(record.producent ?? "")}`}
      style={{ width: "100%", maxWidth: "420px", height: "auto", display: "block", margin: "0 auto" }}
    >
      <circle cx={rearAxlePx.x} cy={rearAxlePx.y} r={wheelRadiusPx} fill="none" stroke="#3a506b" strokeWidth="1.6" />
      <circle cx={frontAxlePx.x} cy={frontAxlePx.y} r={wheelRadiusPx} fill="none" stroke="#3a506b" strokeWidth="1.6" />
      <line x1={rearAxlePx.x} y1={rearAxlePx.y} x2={bbPx.x} y2={bbPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={bbPx.x} y1={bbPx.y} x2={seatTopPx.x} y2={seatTopPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={seatTopPx.x} y1={seatTopPx.y} x2={headTopPx.x} y2={headTopPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={headTopPx.x} y1={headTopPx.y} x2={headBottomPx.x} y2={headBottomPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={headBottomPx.x} y1={headBottomPx.y} x2={bbPx.x} y2={bbPx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={headBottomPx.x} y1={headBottomPx.y} x2={frontAxlePx.x} y2={frontAxlePx.y} stroke="#64ffda" strokeWidth="1.8" />
      <line x1={seatTopPx.x} y1={seatTopPx.y} x2={rearAxlePx.x} y2={rearAxlePx.y} stroke="#64ffda" strokeWidth="1.8" />
    </svg>
  );
};

const EditModal: React.FC<EditModalProps> = ({
  record,
  columns,
  collectionName,
  onSave,
  onClose,
  onDeleteField,
  onDeleteRecord // dodaj do propsów
}) => {
  const [editedRecord, setEditedRecord] = useState<RecordType>({...record});
  const [fieldTypes, setFieldTypes] = useState<Record<string, string>>({});
  const [calculationMessage, setCalculationMessage] = useState<string | null>(null);
  const isBikeRecord = collectionName.toLowerCase() === "bikeframes";

  useEffect(() => {
    setEditedRecord({ ...record });
    setCalculationMessage(null);
  }, [record]);

  // Wykrywanie typu danych dla każdego pola
  useEffect(() => {
    const types: Record<string, string> = {};
    columns.forEach(col => {
      const value = record[col];
      // Określone pola zawsze jako tekst, reszta jako liczby jeśli to możliwe
      if (
        col.toLowerCase() === "producent" ||
        col.toLowerCase() === "model" ||
        col.toLowerCase() === "rozmiar" ||
        col.toLowerCase() === "id"
      ) {
        types[col] = 'text';
      } else if (typeof value === 'number') {
        types[col] = 'number';
      } else if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
        types[col] = 'date';
      } else if (
        typeof value === 'string' &&
        !isNaN(parseFloat(value)) &&
        !isNaN(Number(value))
      ) {
        // Jeśli wartość jest stringiem, ale można ją przekonwertować na liczbę
        types[col] = 'number';
      } else {
        types[col] = 'text';
      }
    });
    setFieldTypes(types);
  }, [record, columns]);


  const handleChange = (column: string, value: string) => {
    let processedValue: any = value;

    // Konwersja wartości na podstawie typu pola
    if (fieldTypes[column] === 'number' && value.trim() !== '') {
      processedValue = parseFloat(value);
      if (isNaN(processedValue)) processedValue = value; // Jeśli nie udało się przekonwertować, zachowaj jako string
    }

    // Ważne: używamy oryginalnej nazwy kolumny dla aktualizacji stanu
    // dzięki temu nie tracimy powiązania między nazwą kolumny a polem formularza
    setEditedRecord(prev => ({
      ...prev,
      [column]: processedValue // Używamy oryginalnej nazwy kolumny, nie normalizowanej
    }));

    // Normalizacja nazwy nastąpi dopiero przy zapisie (handleSubmit)

    // Reset komunikatu o obliczeniach po zmianie dowolnego pola geometrii
    const geometryFields = ['chainstayMm', 'bbdrop', 'reach', 'stack', 'headangledegrees', 'rake', 'trail', 'wheelbase', 'wheelBase'];
    if (geometryFields.includes(column.toLowerCase())) {
      setCalculationMessage(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Przygotuj rekord do zapisu z oryginalnymi kluczami
    const recordToSave: RecordType = { ...editedRecord };

    // Wyślij rekord do zapisania
    onSave(recordToSave);
  };

  // Funkcja sprawdzająca, czy powinniśmy pokazać przycisk kalkulacji wheelbase
  const shouldShowWheelbaseCalculation = (): boolean => {
    // Sprawdź czy rekord już ma wartość wheelBase
    const hasWheelbase = Object.keys(editedRecord).some(key =>
      key.toLowerCase() === 'wheelbase' && editedRecord[key] !== null && editedRecord[key] !== undefined && editedRecord[key] !== ''
    );

    if (hasWheelbase) return false;

    // Sprawdź czy istnieją wymagane pola do obliczenia
    const requiredFieldsBase = ['chainstaymm', 'reach', 'headangledegrees', 'rake', 'stack', 'bbdrop'];
    const hasRequiredBaseFields = requiredFieldsBase.every(field =>
      Object.keys(editedRecord).some(key =>
        key.toLowerCase() === field &&
        editedRecord[key] !== null &&
        editedRecord[key] !== undefined &&
        editedRecord[key] !== ''
      )
    );

    return hasRequiredBaseFields;
  };

  // Funkcja obliczająca wheelbase przy użyciu nowego algorytmu
  const computeWheelbase = () => {
    try {
      // Znajdź klucze pasujące do potrzebnych pól (case insensitive)
      const findValue = (fieldName: string, defaultValue: number | null = null): number => {
        const key = Object.keys(editedRecord).find(k => k.toLowerCase() === fieldName.toLowerCase());
        if (!key) {
          if (defaultValue !== null) return defaultValue;
          throw new Error(`Brakuje pola: ${fieldName}`);
        }

        const value = editedRecord[key];
        if (value === null || value === undefined || value === '') {
          if (defaultValue !== null) return defaultValue;
          throw new Error(`Pole ${fieldName} nie ma wartości`);
        }

        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numValue)) throw new Error(`Wartość pola ${fieldName} nie jest liczbą`);

        return numValue;
      };

      // Pobierz wymagane wartości
      const reach = findValue('reach');
      const headAngleDeg = findValue('headangledegrees');
      const rake = findValue('rake');
      const chainstayMm = findValue('chainstayMm');
      const stack = findValue('stack');
      const bbDrop = findValue('bbdrop');

      // Opcjonalnie pobierz długość główki ramy - jeśli nie ma, używamy prostszego algorytmu
      const headTubeLengthMm = findValue('headtubelengthmm', 0);

      // Weryfikacja wartości
      if (headAngleDeg < 60 || headAngleDeg > 90) {
        throw new Error('Kąt główki (headAngleDegrees) powinien być między 60° a 90°');
      }

      if (chainstayMm <= 0) {
        throw new Error('Długość tylnego widelca (chainstayMm) musi być większa od 0');
      }

      if (rake < 0) {
        throw new Error('Offset widelca (rake) nie może być ujemny');
      }

      if (stack <= 0) {
        throw new Error('Wartość stack musi być większa od 0');
      }

      // Oblicz wheelbase używając nowego algorytmu
      const result = calculateWheelbase(
        chainstayMm,
        bbDrop,
        stack,
        reach,
        headAngleDeg,
        rake,
        headTubeLengthMm
      );

      // Sprawdź czy wystąpił błąd
      if (result.error) {
        throw new Error(result.error);
      }

      // Aktualizujemy rekord z prawidłową nazwą pola (wheelBase z dużą literą B)
      setEditedRecord(prev => ({
        ...prev,
        [WHEELBASE_FIELD_NAME]: result.wheelbase // Używamy stałej z nazwą pola
      }));

      // Ustawiamy komunikat sukcesu
      const headTubeInfo = headTubeLengthMm > 0
        ? ` uwzględniając długość główki ${headTubeLengthMm}mm`
        : ' (bez uwzględnienia długości główki ramy)';

      setCalculationMessage(`Obliczono wheelBase: ${result.wheelbase}mm przy kącie główki ${headAngleDeg}°${headTubeInfo}`);

    } catch (error) {
      console.error('Błąd obliczania wheelBase:', error);
      setCalculationMessage(`Błąd: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // Funkcja kasowania pola w bazie
  const handleDeleteField = (column: string) => {
    if (onDeleteField) {
      onDeleteField(column);
    }
    setEditedRecord(prev => {
      const updated = { ...prev };
      delete updated[column];
      return updated;
    });
  };

  const getRecordTitle = () => {
    if (isBikeRecord) {
      return [
        record.producent ? String(record.producent) : "",
        record.model ? String(record.model) : "",
        record.rok ? String(record.rok) : "",
        record.rozmiar ? String(record.rozmiar) : ""
      ].filter(Boolean).join(" ");
    }

    return [
      record.email ? String(record.email) : "",
      record.name ? String(record.name) : "",
      record.id ? `ID: ${String(record.id)}` : ""
    ].filter(Boolean).join(" ");
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "#112240",
        padding: "20px",
        borderRadius: "8px",
        width: "80%",
        maxWidth: "800px",
        maxHeight: "80vh",
        overflowY: "auto",
        boxShadow: "0 0 20px rgba(0,0,0,0.5)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h2 style={{ color: "#64ffda", marginBottom: "5px" }}>
              {isBikeRecord ? "Edycja roweru" : "Edycja użytkownika"}
            </h2>
            <div style={{ color: "#e6f1ff", fontSize: "1.1rem", fontWeight: "bold" }}>
              {getRecordTitle()}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            <button
              onClick={() => {
                if (onDeleteRecord && record.id) {
                  if (window.confirm("Are you sure you want to delete this bike?")) {
                    onDeleteRecord(record.id);
                  }
                }
              }}
              style={{
                backgroundColor: "#ff3d00",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                padding: "8px 16px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "14px",
                marginBottom: "4px"
              }}
            >
              {isBikeRecord ? "Usuń rower" : "Usuń użytkownika"}
            </button>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "#e6f1ff",
                fontSize: "24px",
                cursor: "pointer"
              }}
            >
              &times;
            </button>
          </div>
        </div>


        {/* Sekcja kalkulatora wheelbase */}
        {isBikeRecord && shouldShowWheelbaseCalculation() && (
          <div style={{
            backgroundColor: "#1a3a59",
            padding: "15px",
            borderRadius: "4px",
            marginBottom: "15px",
            border: "1px solid #64ffda"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p style={{ color: "#64ffda", margin: 0 }}>
                No wheelBase value. You have all data to calculate it.
              </p>
              <button
                onClick={computeWheelbase}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#64ffda",
                  color: "#0a192f",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Calculate wheelBase
              </button>
            </div>
            {calculationMessage && (
              <div style={{
                marginTop: "10px",
                padding: "8px",
                backgroundColor: calculationMessage.startsWith('Error') ? "rgba(255,99,71,0.2)" : "rgba(100,255,218,0.2)",
                borderRadius: "4px",
                color: calculationMessage.startsWith('Error') ? "#ff6347" : "#64ffda"
              }}>
                {calculationMessage}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {isBikeRecord && (
            <div style={{
              backgroundColor: "#0f223a",
              border: "1px solid #233554",
              borderRadius: "8px",
              padding: "12px",
              marginBottom: "14px"
            }}>
              <div style={{ color: "#64ffda", fontWeight: "bold", marginBottom: "8px", fontSize: "13px" }}>Podgląd ramy</div>
              <FrameSizePreview record={editedRecord} width={360} height={170} />
            </div>
          )}

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "8px",
            marginBottom: "18px"
          }}>
            {columns.filter(col => col !== "id" && col !== "ID").map(column => {
              // Wyświetl nazwę pola tak jak w bazie (oryginalny klucz)
              const displayName = column;
              return (
                <div key={column} style={{ marginBottom: "8px", position: "relative" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      color: "#ccd6f6",
                      position: "relative",
                      paddingLeft: "24px",
                      fontSize: "12px"
                    }}
                  >
                    {/* Krzyżyk kasowania pola */}
                    <span
                      title="Usuń pole z bazy"
                      onClick={() => handleDeleteField(column)}
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "18px",
                        height: "18px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: "#888",
                        background: "none",
                        border: "none",
                        borderRadius: "50%",
                        transition: "color 0.2s, background 0.2s"
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.color = "#ff3d00";
                        (e.currentTarget as HTMLElement).style.background = "rgba(255,61,0,0.08)";
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.color = "#888";
                        (e.currentTarget as HTMLElement).style.background = "none";
                      }}
                    >
                      {/* SVG krzyżyk */}
                      <svg width="14" height="14" viewBox="0 0 16 16">
                        <line x1="4" y1="4" x2="12" y2="12" stroke="currentColor" strokeWidth="2" />
                        <line x1="12" y1="4" x2="4" y2="12" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </span>
                    {displayName}
                    {fieldTypes[column] === 'number' &&
                      <span style={{ color: '#64ffda', fontSize: '10px', marginLeft: '4px' }}>(liczba)</span>
                    }
                  </label>
                  <input
                    type={fieldTypes[column] === 'number' ? 'number' : 'text'}
                    value={editedRecord[column] !== null && editedRecord[column] !== undefined ? String(editedRecord[column] || '') : ''}
                    onChange={(e) => handleChange(column, e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      backgroundColor: "#1e2d45",
                      color: "#e6f1ff",
                      border: "1px solid #3a506b",
                      borderRadius: "4px",
                      fontSize: "12px"
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Wyświetlanie komunikatu o obliczeniach (jeśli został ustawiony) */}
          {isBikeRecord && calculationMessage && !shouldShowWheelbaseCalculation() && (
            <div style={{
              marginBottom: "15px",
              padding: "8px",
              backgroundColor: calculationMessage.startsWith('Error') ? "rgba(255,99,71,0.2)" : "rgba(100,255,218,0.2)",
              borderRadius: "4px",
              color: calculationMessage.startsWith('Error') ? "#ff6347" : "#64ffda"
            }}>
              {calculationMessage}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            {isBikeRecord ? (
              <button
                type="button"
                onClick={computeWheelbase}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#64ffda",
                  color: "#0a192f",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Oblicz wheelBase
              </button>
            ) : <div />}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#3a506b",
                  color: "#e6f1ff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#64ffda",
                  color: "#0a192f",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Save changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal;
